import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Folder, File, Search, Trash, LogOut,
  Plus, Download, Image, FileVideo, FileCode,
  FolderPlus, ChevronRight, CheckSquare, ArrowLeft, RefreshCw,
  Sun, Moon, AlertTriangle, Users, Link as LinkIcon
} from 'lucide-react';
import './App.css';

//--- SUB-COMPONENTS
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  const iconProps = { size: 48, strokeWidth: 1.5 };
  const brandIcons = {
    pdf: '/assets/pdf-icon.png',
    docx: '/assets/word-icon.png',
    doc: '/assets/word-icon.png',
    xlsx: '/assets/excel-icon.png',
    xls: '/assets/excel-icon.png',
    zip: '/assets/zip-icon.png',
    rar: '/assets/zip-icon.png',
    php: '/assets/php-icon.png',
  };

  if (brandIcons[ext]) {
    return (
      <img
        src={brandIcons[ext]}
        alt={ext}
        className="brand-icon-img"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }

  const iconMap = {
    png: <Image {...iconProps} className="icon-purple" />,
    jpg: <Image {...iconProps} className="icon-purple" />,
    mp4: <FileVideo {...iconProps} className="icon-orange" />,
    js: <FileCode {...iconProps} className="icon-yellow" />,
  };

  return iconMap[ext] || <File {...iconProps} className="icon-blue" />;
};


const AuthScreen = ({ handleAuth, isLogin, setIsLogin, setAuthData, authData }) => (
  <div className="auth-container dark-theme">
    <div className="auth-card">
      <div className="auth-logo">
        <div className="brand-section-auth" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img 
            src="/assets/logo.png" 
            alt="RND CLOUD Logo" 
            className="brand-logo-img" 
            style={{ height: '60px' }}
            onError={(e) => { e.target.style.display = 'none'; }} 
          />
          <span className="brand-text-auth" style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#fff', letterSpacing: '1px' }}>
            RND CLOUD
          </span>
        </div>
      </div>
      
      <h2 className="auth-title" style={{ marginTop: '20px', color: '#fff' }}>
        {isLogin ? 'Sign In' : 'Create Account'}
      </h2>
      <p className="auth-subtitle" style={{ color: '#94a3b8' }}>to continue to Drive</p>
      
      <form onSubmit={handleAuth}>
        {!isLogin && (
          <input 
            className="auth-input" 
            placeholder="Full Name" 
            onChange={e => setAuthData({ ...authData, username: e.target.value })} 
            required 
          />
        )}
        <input 
          className="auth-input" 
          placeholder="Email" 
          type="email" 
          onChange={e => setAuthData({ ...authData, email: e.target.value })} 
          required 
        />
        <input 
          className="auth-input" 
          placeholder="Password" 
          type="password" 
          onChange={e => setAuthData({ ...authData, password: e.target.value })} 
          required 
        />
        <div className="auth-footer">
          <button type="submit" className="auth-button">Continue</button>
          <div style={{ marginTop: '20px' }}>
            <span className="auth-toggle" style={{ color: '#60a5fa', cursor: 'pointer' }} onClick={() => setIsLogin(!isLogin)}>
              {isLogin ? 'Create account' : 'Sign in instead'}
            </span>
          </div>
        </div>
      </form>
    </div>
  </div>
);

  
function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [theme, setTheme] = useState('dark');
  const [isLogin, setIsLogin] = useState(true);
  const [authData, setAuthData] = useState({ username: "", email: "", password: "" });
  const [items, setItems] = useState({ folders: [], files: [] });
  const [folderPath, setFolderPath] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("drive");
  const [menuPos, setMenuPos] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [storage, setStorage] = useState({ used: 0, limit: 15737418240 });
  const [previewItem, setPreviewItem] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [isCut, setIsCut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharedUsers, setSharedUsers] = useState([]); // Added state
  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);
  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [user] = useState({ id: 1, name: 'Admin' });
  const [currentPath, setFiles] = useState('');


  const [toast, setToast] = useState(null);


const fetchFiles = useCallback(async () => {
  if (!user) return; 
  try {
    const response = await axios.get(`/api/files?path=${currentPath}&userId=${user.id}`); 
    setFiles(response.data); // This now works because setFiles is defined
  } catch (error) {
    console.error("Fetch error:", error);
  }
}, [currentPath, user]); // Include user as a dependency

const extractZip = async (item) => {
  try {
    const response = await axios.post('/api/extract-zip', {
      fileId: item.id,
      path: item.path,
      userId: user.id // Assuming you have user context
    });
    
    if (response.status === 200) {
      setToast({ message: "Extracted successfully!", type: "success" });
      fetchFiles(); // Refresh your file list
    }
  } catch (error) {
    setToast({ message: "Failed to extract ZIP", type: "error" });
  }
};

const handleCompress = async (item) => {
    try {
        const response = await axios.post('/api/compress', {
            itemId: item.id,
            userId: user.id
        });
        if (response.status === 200) {
            setToast({ message: "Folder compressed!", type: "success" });
            fetchFiles(); // Refresh your grid
        }
    } catch (error) {
        setToast({ message: "Failed to compress", type: "error" });
    }
};

// 1. Helper for formatting
const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FilePreviewer = ({ item }) => {
  const [loading, setLoading] = React.useState(true);
  const ext = item.name.split('.').pop().toLowerCase();
  
  // Use absolute URL to ensure PDF/Docs viewers can resolve the path
  const fileUrl = `${window.location.origin}/uploads/${item.path}`;

  const handleLoad = () => setLoading(false);

  return (
    <div className="preview-content-wrapper">
      {loading && (
        <div className="preview-loader">
          <div className="spinner"></div>
          <span>Loading Preview...</span>
        </div>
      )}

      {/* Image Preview */}
      {['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) && (
        <img 
          src={fileUrl} 
          alt={item.name} 
          className="preview-media-element" 
          onLoad={handleLoad}
        />
      )}

      {/* PDF Preview - Using Object for better browser compatibility */}
      {ext === 'pdf' && (
        <object
          data={fileUrl}
          type="application/pdf"
          className="preview-iframe-element"
          onLoad={handleLoad}
        >
          <div className="preview-fallback">
            <p>PDF preview is not supported by your browser.</p>
            <a href={fileUrl} download className="auth-button">Download PDF</a>
          </div>
        </object>
      )}

      {/* Office Files (Word/Excel) */}
      {['doc', 'docx', 'xls', 'xlsx'].includes(ext) && (
        <iframe
          src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
          className="preview-iframe-element"
          title="Office Preview"
          onLoad={handleLoad}
        />
      )}

      {/* Video Preview */}
      {['mp4', 'webm'].includes(ext) && (
        <video 
          controls 
          autoPlay 
          className="preview-media-element" 
          onLoadedData={handleLoad}
        >
          <source src={fileUrl} type={`video/${ext}`} />
        </video>
      )}

      {/* Catch-all for other types */}
      {!['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'mp4', 'webm'].includes(ext) && (
        <iframe
          src={fileUrl}
          className="preview-iframe-element"
          title="Generic Preview"
          onLoad={handleLoad}
        />
      )}
    </div>
  );
};






const Toast = ({ message, type, onClose, onAction }) => (
  <div className={`toast-container animate-bounce-in ${type === 'error' ? 'error' : ''}`}>
    <div className="toast-content">
      <span className="toast-icon">{type === 'error' ? <AlertTriangle size={18} /> : <CheckSquare size={18} />}</span>
      <div className="toast-text-group">
        <p>{message}</p>
        {type === 'error' && onAction && <button className="toast-action-link" onClick={onAction}>Manage Storage</button>}
      </div>
    </div>
    <button onClick={onClose} className="toast-close-btn">×</button>
  </div>
);









  const manageStorage = useCallback(() => {
    setToast(null);
    setSearchTerm("type:large");
  }, []);

  useEffect(() => {
    const percentage = (storage.used / storage.limit) * 100;
    if (percentage >= 90 && !toast) {
      setToast({ message: "Storage is almost full!", type: "error" });
    }
  }, [storage.used, storage.limit, toast]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
  }, []);

  const fetchStorage = useCallback(async () => {
  if (!token) return;
  try {
    const res = await axios.get('/api/storage-stats', { 
      headers: { Authorization: `Bearer ${token}` } 
    });
    
    setStorage({ 
      // Change fallback from 545... to 0
      used: Number(res.data?.used) || 0, 
      limit: Number(res.data?.limit) || 15737418240 
    });
  } catch (err) { 
    console.error("Storage fetch failed", err);
    setStorage({ used: 0, limit: 15737418240 });
  }
}, [token]);

const StorageDisplay = ({ used = 0, limit = 15737418240 }) => {
  const safeUsed = Number(used) || 0;
  const safeLimit = Number(limit) || 15737418240;

  // Conversion logic
  const usedMB = (safeUsed / (1024 * 1024)).toFixed(1);
  const usedGB = (safeUsed / (1024 * 1024 * 1024)).toFixed(1);
  const limitGB = (safeLimit / (1024 * 1024 * 1024)).toFixed(0);
  
  const percentage = Math.min((safeUsed / safeLimit) * 100, 100);

  return (
    <div className="storage-container-inner">
      <div className="storage-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Storage</span>
        <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: percentage > 90 ? '#ef4444' : '#60a5fa' }}>
          {percentage.toFixed(0)}%
        </span>
      </div>
      
      <div className="progress-bg" style={{ height: '6px', backgroundColor: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
        <div 
          className="progress-fill" 
          style={{ 
            width: `${percentage}%`, 
            height: '100%', 
            backgroundColor: percentage > 90 ? '#ef4444' : '#3b82f6',
            transition: 'width 0.5s ease' 
          }}
        ></div>
      </div>

      <p className="storage-text" style={{ fontSize: '0.75rem', marginTop: '8px', color: '#64748b' }}>
        {/* Automatically switch units for better readability */}
        {safeUsed > 1024 * 1024 * 1024 ? `${usedGB} GB` : `${usedMB} MB`} of {limitGB} GB used
      </p>
    </div>
  );
};

  const fetchDrive = useCallback(async (folderId = null) => {
    if (!token) return;
    setIsRefreshing(true);
    try {
      let url = folderId ? `/api/drive/${folderId}` : '/api/drive';
      if (view === 'trash') url += '?trashed=true';
      if (view === 'shared') url = '/api/shared-with-me';
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
      if (folderId && view === 'drive') {
        const pathRes = await axios.get(`/api/folder-path/${folderId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFolderPath(pathRes.data);
      } else { setFolderPath([]); }
      setSelectedItems(new Set());
    } catch (err) { if (err.response?.status === 401) handleLogout(); }
    finally { setTimeout(() => setIsRefreshing(false), 500); }
  }, [token, view, handleLogout]);

  // Added Shared Users fetcher
  const fetchSharedUsers = useCallback(async (item) => {
    if (!item) return;
    try {
      const res = await axios.get(`/api/share-list/${item.type}/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSharedUsers(res.data);
    } catch (err) { console.error("Error fetching users"); }
  }, [token]);

  useEffect(() => {
    fetchDrive(currentFolder);
    fetchStorage();
  }, [currentFolder, fetchDrive, fetchStorage, view]);

  // Trigger fetch when modal opens
  useEffect(() => {
    if (isManageModalOpen && selectedItem) {
      fetchSharedUsers(selectedItem);
    }
  }, [isManageModalOpen, selectedItem, fetchSharedUsers]);

  const handleAuth = async (e) => {
    e.preventDefault();
    const endpoint = isLogin ? '/api/login' : '/api/register';
    try {
      const res = await axios.post(endpoint, authData);
      if (isLogin) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
      } else {
        alert("Success! Log in.");
        setIsLogin(true);
      }
    } catch (err) { alert("Auth failed"); }
  };

  const handleShare = async () => {
    if (!shareEmail || !selectedItem) return;
    try {
      await axios.post('/api/share', {
        itemId: selectedItem.id,
        itemType: selectedItem.type,
        email: shareEmail
      }, { headers: { Authorization: `Bearer ${token}` } });

      setToast({ message: `Shared with ${shareEmail}`, type: "success" });
      setIsShareModalOpen(false);
      setShareEmail("");
    } catch (err) {
      const msg = err.response?.data?.error || "Internal Server Error during sharing.";
      setToast({ message: msg, type: "error" });
    }
  };

  const handleUnshare = async (recipientId) => {
    try {
      await axios.delete('/api/share', {
        data: { itemId: selectedItem.id, itemType: selectedItem.type, sharedWithId: recipientId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setToast({ message: "User removed", type: "success" });
      fetchSharedUsers(selectedItem); // Refresh list
    } catch (err) {
      setToast({ message: "Revoke failed", type: "error" });
    }
  };

  const handleTogglePublic = async () => {
    if (!selectedItem) return;
    try {
      await axios.patch(`/api/files/${selectedItem.id}/public`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      setIsManageModalOpen(false);
      setToast({ message: "Visibility updated", type: "success" });
    } catch (err) { alert("Failed to change visibility"); }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/preview/${selectedItem.id}`;
    navigator.clipboard.writeText(link).then(() => {
      setToast({ message: "Link copied to clipboard", type: "success" });
    });
    setMenuPos(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const controller = new AbortController();
    setUploadController(controller);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) formData.append('folderId', currentFolder);
    setUploadProgress(1);
    try {
      await axios.post('/api/upload', formData, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        onUploadProgress: (pe) => setUploadProgress(Math.round((pe.loaded * 100) / pe.total))
      });
      fetchDrive(currentFolder);
      fetchStorage();
      setToast({ message: "Upload complete!", type: "success" });
    } catch (err) { if (!axios.isCancel(err)) alert("Upload failed"); }
    finally { setUploadProgress(0); setUploadController(null); }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const controller = new AbortController();
    setUploadController(controller);
    const formData = new FormData();
    files.forEach(f => {
      formData.append('files', f);
      formData.append('paths', f.webkitRelativePath);
    });
    if (currentFolder) formData.append('parentId', currentFolder);
    setUploadProgress(1);
    try {
      await axios.post('/api/upload-folder', formData, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        onUploadProgress: (pe) => setUploadProgress(Math.round((pe.loaded * 100) / pe.total))
      });
      fetchDrive(currentFolder);
      fetchStorage();
    } catch (err) { if (!axios.isCancel(err)) alert("Folder upload failed"); }
    finally { setUploadProgress(0); setUploadController(null); }
  };

  const handlePaste = async (e) => {
    if (e) e.stopPropagation();
    if (!clipboard) return;
    try {
      await axios.post('/api/paste', {
        itemId: clipboard.id, itemType: clipboard.type,
        destinationFolderId: currentFolder, action: isCut ? 'move' : 'copy'
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      fetchStorage();
      if (isCut) setClipboard(null);
      setMenuPos(null);
    } catch (err) { alert("Paste failed"); }
  };

  const handleContextMenu = (e, item = null) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItem(item);
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  const handleRename = async (item) => {
    const newName = prompt(`Rename ${item.type}:`, item.name);
    if (!newName) return;
    const endpoint = item.type === 'folder' ? `/api/rename/folder/${item.id}` : `/api/rename/${item.id}`;
    try {
      await axios.put(endpoint, { newName }, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      setMenuPos(null);
    } catch (err) { alert("Rename failed"); }
  };

  const handleSoftDelete = async (item) => {
    if (!window.confirm(`Move ${item.name} to trash?`)) return;
    const endpoint = item.type === 'folder' ? `/api/trash/folder/${item.id}` : `/api/trash/${item.id}`;
    try {
      await axios.put(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      fetchStorage();
      setMenuPos(null);
    } catch (err) { alert("Action failed"); }
  };

  const handleRestore = async (item) => {
    const endpoint = item.type === 'folder' ? `/api/restore/folder/${item.id}` : `/api/restore/${item.id}`;
    try {
      await axios.put(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      setMenuPos(null);
    } catch (err) { alert("Restore failed"); }
  };

  const handlePermanentDelete = async (item) => {
    if (!window.confirm(`Permanently delete ${item.name}?`)) return;
    const endpoint = item.type === 'folder' ? `/api/delete/folder/${item.id}` : `/api/delete/${item.id}`;
    try {
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      fetchDrive(currentFolder);
      fetchStorage();
      setMenuPos(null);
    } catch (err) { alert("Delete failed"); }
  };

  const handleSingleDownload = async (item) => {
    setDownloadProgress(1);
    try {
      const url = item.type === 'folder' ? `/api/download/folder/${item.id}` : `/uploads/${item.path}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        onDownloadProgress: (pe) => { if (pe.total) setDownloadProgress(Math.round((pe.loaded * 100) / pe.total)); }
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.setAttribute('download', item.type === 'folder' ? `${item.name}.zip` : item.name);
      document.body.appendChild(link);
      link.click();
    } catch (err) { alert("Download failed"); }
    finally { setDownloadProgress(0); setMenuPos(null); }
  };

  const handleBulkDownload = async () => {
    const fIds = [], folIds = [];
    selectedItems.forEach(k => {
      const [type, id] = k.split('-');
      type === 'file' ? fIds.push(id) : folIds.push(id);
    });
    setDownloadProgress(1);
    try {
      const res = await axios.post('/api/download/bulk', { fileIds: fIds, folderIds: folIds }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        onDownloadProgress: (pe) => { if (pe.total) setDownloadProgress(Math.round((pe.loaded * 100) / pe.total)); }
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.setAttribute('download', 'drive_download.zip');
      document.body.appendChild(link);
      link.click();
    } catch (err) { alert("Download failed"); }
    finally { setDownloadProgress(0); }
  };

  const toggleSelect = (item) => {
    const next = new Set(selectedItems);
    const key = `${item.type}-${item.id}`;
    next.has(key) ? next.delete(key) : next.add(key);
    setSelectedItems(next);
  };

  const filteredFolders = items.folders.filter(f => {
    if (searchTerm === "type:large") return false;
    return f.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredFiles = items.files.filter(f => {
    if (searchTerm === "type:large") return f.size > 10 * 1024 * 1024;
    return f.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (!token) return <AuthScreen handleAuth={handleAuth} isLogin={isLogin} setIsLogin={setIsLogin} setAuthData={setAuthData} authData={authData} />;

  return (
    <div className={`app-container ${theme}-theme`} onClick={() => setMenuPos(null)}>
      {/* ADDED: MOBILE HEADER FROM PIC 1 */}
      <div className="mobile-top-bar">
        <div className="mobile-brand-container">
          <img src="/assets/logo.png" alt="Logo" className="mobile-logo-img" />
          <span className="mobile-brand-text">RND CLOUD</span>
        </div>
        <div className="mobile-search-container">
          <Search size={18} className="icon-gray" />
          <input 
            placeholder="Search in Drive" 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
          />
        </div>
      </div>

      <aside className="sidebar" onClick={e => e.stopPropagation()}>
        <div className="brand-section">
          <a 
            href="/" 
            className="brand-link" 
            onClick={(e) => {
              e.preventDefault(); 
              setView("drive");
              setCurrentFolder(null);
              setSearchTerm("");
            }}
          >
            <img 
              src="/assets/logo.png" 
              alt="RND CLOUD Logo" 
              className="brand-logo-img" 
              onError={(e) => { e.target.style.display = 'none'; }} 
            />
            <span className="brand-text">RND CLOUD</span>
          </a>
        </div>
        <button onClick={toggleTheme} className="theme-toggle-sidebar-btn">
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
        <div className="new-btn-container">
          <button onClick={() => { const n = prompt("Name:"); n && axios.post('/api/folder', { name: n, parentId: currentFolder }, { headers: { Authorization: `Bearer ${token}` } }).then(() => fetchDrive(currentFolder)) }} className="new-btn folder-upload">
            <FolderPlus size={20} className="icon-green" /> <span>New Folder</span>
          </button>
          <label className="new-btn folder-upload-btn">
            <Folder size={20} className="icon-orange" /> <span>Upload Folder</span>
            <input type="file" className="hidden" webkitdirectory="true" directory="true" onChange={handleFolderUpload} />
          </label>
          <label className="new-btn file-upload">
            <Plus size={24} className="icon-blue" /> <span>File Upload</span>
            <input type="file" className="hidden" onChange={handleUpload} />
          </label>
        </div>
        <nav className="sidebar-nav">
          <button onClick={() => { setView("drive"); setCurrentFolder(null); setSearchTerm(""); }} className={`sidebar-item ${view === 'drive' ? 'active' : ''}`}>
            <Folder size={18} className="icon-margin" /> My Drive
          </button>
          <button onClick={() => { setView("shared"); setCurrentFolder(null); setSearchTerm(""); }} className={`sidebar-item ${view === 'shared' ? 'active' : ''}`}>
            <Users size={18} className="icon-margin" /> Shared with me
          </button>
          <button onClick={() => { setView("trash"); setCurrentFolder(null); setSearchTerm(""); }} className={`sidebar-item ${view === 'trash' ? 'active' : ''}`}>
            <Trash size={18} className="icon-margin" /> Trash
          </button>
        </nav>
        <div className="storage-section">
          <StorageDisplay used={storage.used} limit={storage.limit} />
          <button onClick={handleLogout} className="logout-btn"><LogOut size={14} /> Logout</button>
        </div>
      </aside>

      <main className="main-content" onContextMenu={(e) => handleContextMenu(e, null)}>
        <header className="header-search">
          <div className="search-box">
            <Search size={20} className="icon-gray" />
            <input placeholder="Search in Drive" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
        </header>

        <div className="action-bar">
          <div className="action-left">
            {currentFolder && <button onClick={() => setCurrentFolder(null)} className="back-btn"><ArrowLeft size={16} /> Back</button>}
            <div className="breadcrumb">
              {!currentFolder && <span className="breadcrumb-text">{view === 'shared' ? 'Shared with me' : 'My Drive'}</span>}
              {folderPath.map(f => (
                <div key={f.id} className="breadcrumb-segment">
                  <ChevronRight size={14} className="icon-gray-light" />
                  <button onClick={() => setCurrentFolder(f.id)} className="breadcrumb-link">{f.name}</button>
                </div>
              ))}
            </div>
            {isRefreshing && <RefreshCw size={16} className="refresh-spinner" />}
          </div>
          {selectedItems.size > 0 && <button onClick={handleBulkDownload} className="download-all-btn"><Download size={16} /> Download Zip</button>}
        </div>

        <div className={`file-grid ${isRefreshing ? 'is-refreshing' : ''}`}>
          {filteredFolders.map(folder => (
            <div key={folder.id} className={`file-card ${selectedItems.has(`folder-${folder.id}`) ? 'selected' : ''}`} onDoubleClick={() => view !== 'trash' && setCurrentFolder(folder.id)} onClick={(e) => { e.stopPropagation(); toggleSelect({ ...folder, type: 'folder' }) }} onContextMenu={(e) => handleContextMenu(e, { ...folder, type: 'folder' })} >
              <div className="file-preview"><Folder size={64} className="text-amber-400 fill-amber-400" strokeWidth={1} /></div>
              <div className="file-info">
                <span>{folder.name}</span>
                {view === 'shared' && <span className="shared-by-tag">Shared by {folder.owner_user} ({folder.owner_email})</span>}
              </div>
            </div>
          ))}
          {filteredFiles.map(file => (
            <div key={file.id} className={`file-card ${selectedItems.has(`file-${file.id}`) ? 'selected' : ''}`} onDoubleClick={() => setPreviewItem(file)} onClick={(e) => { e.stopPropagation(); toggleSelect({ ...file, type: 'file' }) }} onContextMenu={(e) => handleContextMenu(e, { ...file, type: 'file' })} >
              <div className="file-preview">
                {getFileIcon(file.name)}
                {file.sharedWithMe && <div className="shared-indicator"><Users size={12} /></div>}
              </div>
              <div className="file-info">
                <span>{file.name}</span>
                {view === 'shared' && <span className="shared-by-tag">Shared by {file.owner_user} ({file.owner_email})</span>}
              </div>
            </div>
          ))}
        </div>
      </main>

      {menuPos && (
        <div className="win-context-menu" style={{ top: menuPos.y, left: menuPos.x }} onClick={e => e.stopPropagation()}>
          {selectedItem ? (
            view === 'trash' ? (
              <>
                <div className="win-menu-item" onClick={() => handleRestore(selectedItem)}>Restore</div>
                <div className="win-menu-item win-delete" onClick={() => handlePermanentDelete(selectedItem)}>Delete Permanently</div>
              </>
            ) : view === 'shared' ? (
              <>
                <div className="win-menu-item" onClick={() => handleSingleDownload(selectedItem)}>Download</div>
              </>
            ) : (
              <>
                <div className="win-menu-item" onClick={() => handleSingleDownload(selectedItem)}>Download</div>
                <div className="win-menu-item" onClick={() => { setIsShareModalOpen(true); setMenuPos(null); }}>Share</div>
                <div className="win-menu-item" onClick={() => { setIsManageModalOpen(true); setMenuPos(null); }}>Manage Access</div>
                <div className="win-menu-item" onClick={handleCopyLink}>Copy Link</div>
                <hr className="win-divider" />
                <div className="win-menu-item" onClick={() => { setClipboard(selectedItem); setIsCut(true); setMenuPos(null); }}>Cut</div>
                <div className="win-menu-item" onClick={() => { setClipboard(selectedItem); setIsCut(false); setMenuPos(null); }}>Copy</div><div className="win-menu-item" onClick={() => { handleCompress(selectedItem); setMenuPos(null); }}><div className="menu-icon-container"><FolderPlus size={16} color="#00a884" /> {/* WhatsApp Green */}</div>Compress to ZIP</div>
                <div className="win-menu-item" onClick={() => { extractZip(selectedItem); setMenuPos(null); }}><div className="menu-icon-container"><FolderPlus size={16} /></div>Extract Here</div>
                <div className="win-menu-item" onClick={() => handleRename(selectedItem)}>Rename</div>
                <div className="win-menu-item win-delete" onClick={() => handleSoftDelete(selectedItem)}>Delete</div>
                <hr className="win-divider" />
                <div className="win-menu-item" onClick={() => { setIsPropertiesModalOpen(true); setMenuPos(null); }}>Properties</div>
              </>
            )
          ) : (
            <>
              <div className="win-menu-item" onClick={() => { fetchDrive(currentFolder); setMenuPos(null); }}>Refresh</div>
              <hr className="win-divider" />
              <div className={`win-menu-item ${!clipboard ? 'win-disabled' : ''}`} onClick={handlePaste}>Paste</div>
            </>
          )}
        </div>
      )}

      {isShareModalOpen && (
        <div className="modal-overlay" onClick={() => setIsShareModalOpen(false)}>
          <div className="modal-content share-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share "{selectedItem?.name}"</h3>
              <button onClick={() => setIsShareModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body share-body">
              <div className="share-input-group">
                <input className="auth-input share-field" placeholder="User email" value={shareEmail} onChange={e => setShareEmail(e.target.value)} />
                <button onClick={handleShare} className="auth-button share-confirm">Share</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RE-INTEGRATED MANAGE MODAL */}
      {isManageModalOpen && (
        <div className="modal-overlay" onClick={() => setIsManageModalOpen(false)}>
          <div className="modal-content share-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage access: {selectedItem?.name}</h3>
              <button onClick={() => setIsManageModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              {/* General Access Row */}
              <div className="link-row">
                <div className="link-icon-circle"><LinkIcon size={18} /></div>
                <div className="link-text">
                  <p className="access-name">General access</p>
                  <p className="access-role">{selectedItem?.isPublic ? 'Anyone with link' : 'Restricted'}</p>
                </div>
                <button className="copy-link-btn" onClick={handleTogglePublic}>
                  {selectedItem?.isPublic ? 'Make Private' : 'Make Public'}
                </button>
              </div>

              <hr className="win-divider" />

              <p className="section-title">People with access</p>
              <div className="user-list-container">
                {sharedUsers.length === 0 ? (
                  <p className="no-users-text">Not shared with anyone yet.</p>
                ) : (
                  sharedUsers.map(user => (
                    <div key={user.id} className="user-access-row">
                      <div className="user-info-main">
                        <div className="user-avatar-small">{user.email[0].toUpperCase()}</div>
                        <span className="user-email-text">{user.email}</span>
                      </div>
                      <button className="revoke-btn" onClick={() => handleUnshare(user.id)}>
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} onAction={toast.type === 'error' ? manageStorage : null} />}

      {(downloadProgress > 0 || uploadProgress > 0) && (
        <div className="progress-panel">
          <div className="progress-label">
            <span>{uploadProgress > 0 ? 'Uploading...' : 'Downloading...'} {uploadProgress || downloadProgress}%</span>
            {uploadController && <button className="cancel-upload-btn" onClick={() => uploadController.abort()}>Cancel</button>}
          </div>
          <div className="progress-bg">
            <div className="progress-fill blue" style={{ width: `${uploadProgress || downloadProgress}%` }}></div>
          </div>
        </div>
      )}
{/* --- PROPERTIES MODAL --- */}
{isPropertiesModalOpen && selectedItem && (
  <div className="modal-overlay" onClick={() => setIsPropertiesModalOpen(false)}>
    <div className="modal-content share-modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h3>{selectedItem.type === 'folder' ? 'Folder' : 'File'} Properties</h3>
        <button onClick={() => setIsPropertiesModalOpen(false)} className="close-btn">×</button>
      </div>
      <div className="modal-body properties-view">
        <div className="prop-row">
          <span className="prop-label">Name:</span>
          <span className="prop-value">{selectedItem.name}</span>
        </div>
        <div className="prop-row">
          <span className="prop-label">Type:</span>
          <span className="prop-value">{selectedItem.type === 'folder' ? 'File Folder' : `${selectedItem.name.split('.').pop().toUpperCase()} File`}</span>
        </div>
        <hr className="win-divider" />
        <div className="prop-row">
          <span className="prop-label">Size:</span>
          <span className="prop-value">{selectedItem.type === 'folder' ? '--' : formatSize(selectedItem.size)}</span>
        </div>
        <div className="prop-row">
          <span className="prop-label">Created:</span>
          <span className="prop-value">{new Date(selectedItem.created_at || Date.now()).toLocaleString()}</span>
        </div>
      </div>
    </div>
  </div>
)}

{/* --- ENHANCED PREVIEW MODAL (Replacing your old one) --- */}
{previewItem && (
  <div className="modal-overlay" onClick={() => setPreviewItem(null)}>
    <div className="modal-content preview-modal-content" style={{ width: '90%', height: '90%', maxWidth: 'none' }} onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>{previewItem.name}</h3>
        </div>
        <button onClick={() => setPreviewItem(null)} className="close-btn">×</button>
      </div>
      <div className="modal-body" style={{ height: 'calc(100% - 60px)', padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
        <FilePreviewer item={previewItem} />
      </div>
    </div>
  </div>
)}
     {/* Add this state at the top of your App function if not present */}
<div className="mobile-bottom-nav">
  <div className={`nav-item ${view === 'drive' ? 'active' : ''}`} onClick={() => { setView("drive"); setCurrentFolder(null); setShowUploadMenu(false); }}>
    <Folder size={20} />
    <span>My Drive</span>
  </div>

  <div className={`nav-item ${view === 'shared' ? 'active' : ''}`} onClick={() => { setView("shared"); setCurrentFolder(null); setShowUploadMenu(false); }}>
    <Users size={20} />
    <span>Shared</span>
  </div>

  {/* Central Action with Sub-Menu */}
  <div className="nav-item upload-center">
    {showUploadMenu && (
      <div className="mobile-upload-options">
        <div className="mobile-opt" onClick={() => { document.querySelector('.file-upload input').click(); setShowUploadMenu(false); }}>
          <Plus size={18} /> File
        </div>
        <div className="mobile-opt" onClick={() => { document.querySelector('.folder-upload-btn input').click(); setShowUploadMenu(false); }}>
          <Folder size={18} /> Folder
        </div>
      </div>
    )}
    <div className="upload-circle" onClick={() => setShowUploadMenu(!showUploadMenu)}>
      <Plus size={24} color="white" style={{ transform: showUploadMenu ? 'rotate(45deg)' : 'rotate(0)', transition: '0.2s' }} />
    </div>
    <span>Upload</span>
  </div>

  <div className={`nav-item ${view === 'trash' ? 'active' : ''}`} onClick={() => { setView("trash"); setCurrentFolder(null); setShowUploadMenu(false); }}>
    <Trash size={20} />
    <span>Trash</span>
  </div>

  <div className="nav-item" onClick={handleLogout}>
    <LogOut size={20} color="#ef4444" />
    <span style={{ color: '#ef4444' }}>Logout</span>
  </div>
</div>
    </div>
  );
}

export default App;